import { RegularSelectInput, RichEditor, TextInput } from "components/core";
import { FormikProps, useFormik } from "formik";
import { Disclosure } from "@headlessui/react";
import _ from "lodash";
import { FC, useMemo } from "react";
import { wrapClick } from "utils";
import * as Yup from "yup";
import {
  ChevronUpIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"; // Added PlusIcon

const validationSchema = Yup.object().shape({
  treaties: Yup.array()
    .of(
      Yup.object().shape({
        policy_number: Yup.string().required("Policy number is required"),
        claim_number: Yup.string().required("Claim number is required"),
        date_of_loss: Yup.string().required("Date of loss is required"),
        insured: Yup.string().required("Insured is required"),
        claim_paid: Yup.number()
          .typeError("Must be a number")
          .required("Claim paid is required"),
        layer_number: Yup.string().required("Layer number is required"),
        expected_deductible: Yup.number()
          .typeError("Must be a number")
          .required("Deductible is required"), // Added validation for deductible
        treaty_claim_details: Yup.object().shape({
          third_party_claim_amount: Yup.number()
            .typeError("Must be a number")
            .nullable()
            .notRequired(), // Allow null/empty
          survey_fees: Yup.number()
            .typeError("Must be a number")
            .nullable()
            .notRequired(),
          legal_fees: Yup.number()
            .typeError("Must be a number")
            .nullable()
            .notRequired(),
          salvage: Yup.number()
            .typeError("Must be a number")
            .nullable()
            .notRequired(),
          company_name: Yup.string().notRequired(),
          net_liability_amount: Yup.number()
            .typeError("Must be a number")
            .nullable()
            .notRequired(),
        }),
        treaty_comment: Yup.string().required("Comment is required"),
      })
    )
    .min(1, "At least one claim is required"), // Ensure at least one claim exists
});

// Define a type for a single treaty claim within the form
type TreatyClaim = {
  policy_number: string;
  claim_number: string;
  date_of_loss: string;
  insured: string;
  claim_paid: string | number; // Allow string initially for input, convert later if needed
  layer_number: string;
  expected_deductible: string | number; // Allow string initially
  treaty_claim_details: {
    third_party_claim_amount?: string | number;
    survey_fees?: string | number;
    legal_fees?: string | number;
    salvage?: string | number;
    company_name?: string;
    net_liability_amount?: string | number;
  };
  treaty_comment: string;
  treaty_id?: string; // Add treaty_id for key prop
};

// Define the shape of the form values
type FormValues = {
  treaties: TreatyClaim[];
};

type Props = {
  handleNext: (values: FormValues) => void; // Use defined type
  handlePrevious: () => void;
  // initialValues: any; // This seems unused if we derive from parentForm
  // values: any; // This seems unused if we derive from parentForm
  handleCancel: () => void;
  parentForm: FormikProps<any>; // Keep parentForm flexible for now
};

const TreatyClaimForm: FC<Props> = ({
  handleCancel,
  handleNext,
  handlePrevious,
  parentForm,
}) => {
  const form = useFormik<FormValues>({
    // Ensure initialValues conform to FormValues, defaulting to an empty array if needed
    initialValues: {
      treaties: _.get(parentForm.values, "claims.treaties", [
        // Start with one default claim if none exist
        {
          policy_number:
            parentForm?.values?.treaty?.selectedTreaty?.treaty_reference || "",
          claim_number: "",
          date_of_loss: "",
          insured: "",
          claim_paid: "",
          layer_number: "",
          expected_deductible: "",
          treaty_claim_details: {
            third_party_claim_amount: "",
            survey_fees: "",
            legal_fees: "",
            salvage: "",
            company_name: "",
            net_liability_amount: "",
          },
          treaty_comment: "",
          treaty_id: `new-${Date.now()}`, // Add a temporary unique ID for the key prop
        },
      ]),
    },
    onSubmit: (values) => {
      // Optional: Perform any final data transformation before passing up
      handleNext(values);
    },
    validationSchema,
    enableReinitialize: true, // Allow reinitialization if parentForm values change
    onReset: () => {
      handleCancel(); // Call the cancel handler passed via props
    },
  });

  // Function to add a new claim entry
  const addClaim = () => {
    const newClaim: TreatyClaim = {
      policy_number:
        parentForm?.values?.treaty?.selectedTreaty?.treaty_reference || "",
      claim_number: "",
      date_of_loss: "",
      insured: "",
      claim_paid: "",
      layer_number: "",
      expected_deductible: "",
      treaty_claim_details: {
        /* default empty details */
      },
      treaty_comment: "",
      treaty_id: `new-${Date.now()}-${Math.random()}`, // Unique key for React list
    };
    form.setFieldValue("treaties", [...form.values.treaties, newClaim]);
  };

  // Function to remove a claim entry
  const removeClaim = (index: number) => {
    const updatedTreaties = form.values.treaties.filter((_, i) => i !== index);
    form.setFieldValue("treaties", updatedTreaties);
  };

  // Memoize layer options to prevent recalculation on every render
  const layerOptions = useMemo(() => {
    try {
      const layers =
        JSON.parse(
          parentForm?.values?.treaty?.selectedTreaty?.layer_limit || "[]"
        ) || [];
      return layers.map((_: any, i: number) => ({
        value: (i + 1).toString(), // Ensure value is string if select expects string
        label: `Layer ${i + 1}`,
      }));
    } catch (e) {
      return []; // Return empty array on error
    }
  }, [parentForm?.values?.treaty?.selectedTreaty?.layer_limit]);

  return (
    // Use h-full and flex-col to manage height and layout
    <div className="flex h-full flex-1 flex-col bg-white dark:bg-gray-900">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Reinsured Info Box - Improved Styling */}
          <div className="rounded-lg border border-dashed border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Reinsured
                </span>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {parentForm?.values?.treaty?.selectedTreaty?.insurer
                    ?.insurer_company_name || "N/A"}
                </p>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Reinsured Email
                </span>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {parentForm?.values?.treaty?.selectedTreaty?.insurer
                    ?.insurer_company_email || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Claims Section Title */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Claims
          </h2>

          {/* Disclosures for Each Claim */}
          <div className="space-y-4">
            {form.values.treaties?.map((claim, index) => (
              <Disclosure
                as="div"
                key={claim.treaty_id || index}
                defaultOpen={index === 0}
                className="rounded-lg border border-gray-200 dark:border-gray-700"
              >
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium ${open ? "bg-primary-50 dark:bg-primary-900/20" : "bg-gray-100 dark:bg-gray-800"} rounded-t-lg text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75`}
                    >
                      <span className="font-semibold">{`Claim #${index + 1}`}</span>
                      <div className="flex items-center space-x-3">
                        {/* Allow deleting only if more than one claim exists */}
                        {form.values.treaties.length > 1 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent disclosure toggle
                              removeClaim(index);
                            }}
                            className="text-red-600 hover:text-red-800 dark:text-red-500 dark:hover:text-red-400"
                            aria-label={`Remove Claim #${index + 1}`}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                        <ChevronUpIcon
                          className={`${!open ? "rotate-180 transform" : ""} h-5 w-5 text-gray-500 dark:text-gray-400`}
                        />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 rounded-b-lg">
                      <div className="space-y-5">
                        {/* Basic Claim Info */}
                        <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
                          <TextInput
                            type="text"
                            id={`treaties.${index}.policy_number`}
                            label="Policy Number"
                            disabled // Assuming this should remain disabled
                            required={true}
                            {...form}
                          />
                          <TextInput
                            type="text"
                            id={`treaties.${index}.claim_number`}
                            label="Claim Number"
                            placeholder="Enter claim number"
                            required={true}
                            {...form}
                          />
                          <TextInput
                            type="date"
                            id={`treaties.${index}.date_of_loss`}
                            label="Date of Loss"
                            required={true}
                            {...form}
                          />
                          <TextInput
                            type="text"
                            id={`treaties.${index}.insured`}
                            label="Insured"
                            placeholder="Enter name of insured"
                            required={true}
                            {...form}
                          />
                          <div className="sm:col-span-2">
                            <TextInput
                              type="number" // Use number for currency/amounts
                              step={0.01} // Allow decimals
                              id={`treaties.${index}.claim_paid`}
                              label="Claim Paid (Amount)"
                              placeholder="e.g., 10000.00"
                              required={true}
                              {...form}
                            />
                          </div>
                          <RegularSelectInput
                            options={layerOptions}
                            id={`treaties.${index}.layer_number`}
                            label="Layer Number"
                            placeholder="Select Layer"
                            required={true}
                            {...form}
                          />
                          <TextInput
                            type="number"
                            step={0.01}
                            id={`treaties.${index}.expected_deductible`}
                            label="Deductible (Amount)"
                            placeholder="e.g., 1200.00"
                            required={true}
                            {...form}
                          />
                        </div>

                        {/* Claim Details Subsection */}
                        <div className="space-y-4 rounded-md border border-gray-200 dark:border-gray-700 p-4">
                          <h3 className="text-base font-medium text-gray-800 dark:text-gray-200">
                            Additional Claim Details (Optional)
                          </h3>
                          <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
                            <TextInput
                              type="number"
                              step={0.01}
                              id={`treaties.${index}.treaty_claim_details.third_party_claim_amount`}
                              label="Third Party Claim Amount (+)"
                              placeholder="Enter amount"
                              {...form}
                            />
                            <TextInput
                              type="number"
                              step={0.01}
                              id={`treaties.${index}.treaty_claim_details.survey_fees`}
                              label="Survey Fees (+)"
                              placeholder="Enter amount"
                              {...form}
                            />
                            <TextInput
                              type="number"
                              step={0.01}
                              id={`treaties.${index}.treaty_claim_details.legal_fees`}
                              label="Legal Fees (+)"
                              placeholder="Enter amount"
                              {...form}
                            />
                            <TextInput
                              type="number"
                              step={0.01}
                              id={`treaties.${index}.treaty_claim_details.salvage`}
                              label="Salvage (-)"
                              placeholder="Enter amount"
                              {...form}
                            />
                            <TextInput
                              type="text"
                              id={`treaties.${index}.treaty_claim_details.company_name`}
                              label="Company Name"
                              placeholder="Enter company name"
                              {...form}
                            />
                            <TextInput
                              type="number"
                              step={0.01}
                              id={`treaties.${index}.treaty_claim_details.net_liability_amount`}
                              label="Net Liability Amount (+)"
                              placeholder="Enter amount"
                              {...form}
                            />
                          </div>
                        </div>

                        {/* Claim Comment */}
                        <div>
                          <RichEditor
                            id={`treaties.${index}.treaty_comment`}
                            label="Claim Comment"
                            placeholder="Provide details about the claim..."
                            required={true}
                            {...form}
                          />
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}

            {/* Add Another Claim Button - Improved Styling */}
            <button
              type="button"
              onClick={wrapClick(addClaim)}
              className="flex w-full items-center justify-center space-x-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-gray-600 hover:border-primary-500 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary-500 dark:hover:text-primary-400"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Another Claim</span>
            </button>
          </div>
        </div>
      </div>

      {/* Footer Actions - Improved Styling and Layout */}
      <div className="flex flex-shrink-0 justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          onClick={wrapClick(form.resetForm)} // Use form's reset which calls handleCancel
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={wrapClick(handlePrevious)}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={wrapClick(form.handleSubmit)}
          disabled={!form.isValid || form.isSubmitting} // Disable while submitting or invalid
          className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {form.isSubmitting ? "Submitting..." : "Next"}
        </button>
      </div>
    </div>
  );
};

export default TreatyClaimForm;
